import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Observable, of, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, switchMap, map, catchError } from 'rxjs/operators';
import { LibraryModule } from '../shared/lib/lib.module';
import { FormApiService } from '../core/services/formapiService';
import { GoogleMapsLoaderService } from '../core/services/google-maps-loader.service';
import { RegistrationSuccessModalComponent } from './components/registration-success-modal.component';
import { LoginModalService } from '../core/services/login-modal.service';
import { FormDateUtils } from '../shared/utils/form-date-utils';
import { ParticipantLoginModalComponent } from './components/participant-login-modal.component';

@Component({
  selector: 'register-component, RegisterComponent',
  templateUrl: './register.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LibraryModule,
    RegistrationSuccessModalComponent,
    ParticipantLoginModalComponent,
  ],
  styles: [`
    :host ::ng-deep ni-text-field[formControlName="phone"] > div > div:last-child > div:last-child {
      display: none !important;
    }
    :host ::ng-deep .certify-checkbox > div {
      align-items: flex-start !important;
    }
    :host ::ng-deep .certify-checkbox .flex.flex-row label span:last-child {
      line-height: 1.5;
      flex: 1;
    }
  `]
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  currentDate: Date = new Date();
  /** Latest birth date allowed (17 years ago) - restricts account creation to age 17+ */
  maxBirthDateForAge17: Date = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 17);
    return d;
  })();
  isMailingAddressDifferent: boolean = false;
  private readonly addressSearchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  /** Cache last SSN we validated to avoid calling the API again for the same value */
  private lastCheckedSsn: string | null = null;
  private lastCheckResult: ValidationErrors | null = null;
  
  // Field label mapping for user-friendly error messages
  private readonly fieldLabels: { [key: string]: string } = {
    'email': 'Email',
    'confirmEmail': 'Confirm Email',
    'phone': 'Phone',
    'certifyAccuracy': 'Certification',
    'addresses.0.postalCode': 'Postal Code',
    'addresses.0.city': 'City',
    'addresses.0.state': 'State',
    'addresses.0.country': 'Country',
    'addresses.0.county': 'County',
    'addresses.1.postalCode': 'Postal Code',
    'addresses.1.city': 'City',
    'addresses.1.state': 'State',
    'addresses.1.country': 'Country',
    'addresses.1.county': 'County',
    'contacts.0.firstName': 'First Name',
    'contacts.0.lastName': 'Last Name',
    'contacts.0.dob': 'Date of Birth',
    'contacts.0.confirmDob': 'Confirm Date of Birth',
    'contacts.0.email': 'Email',
    'contacts.0.gender': 'Gender',
    'contacts.0.race': 'Race',
    'contacts.0.ethinicity': 'Ethnicity',
    'contacts.0.militaryStatus': 'Military Status',
    'contacts.0.isMigrantFarmWorker': 'Migrant Seasonal Farm Worker',
    'contacts.0.isDisabled': 'This option is required',
    'contacts.0.citizenship': 'Citizenship Status',
    'contacts.0.isReceivingIncome': 'This option is required',
    'contacts.0.ssn': 'Identification Number',
    'contacts.0.confirmSsn': 'Confirm Identification Number',
    'contacts.0.identificationType': 'Identification Type',
  };
  genderOptions = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Nonbinary', value: 'Nonbinary' },
    { label: 'Not Listed', value: 'Not Listed' },
  ];
  COUNTRIES = [
    { label: 'Afghanistan', value: 'AF' },
    { label: 'Albania', value: 'AL' },
    { label: 'Algeria', value: 'DZ' },
    { label: 'Andorra', value: 'AD' },
    { label: 'Angola', value: 'AO' },
    { label: 'Argentina', value: 'AR' },
    { label: 'Armenia', value: 'AM' },
    { label: 'Australia', value: 'AU' },
    { label: 'Austria', value: 'AT' },
    { label: 'Azerbaijan', value: 'AZ' },
    { label: 'Bahamas', value: 'BS' },
    { label: 'Bahrain', value: 'BH' },
    { label: 'Bangladesh', value: 'BD' },
    { label: 'Barbados', value: 'BB' },
    { label: 'Belarus', value: 'BY' },
    { label: 'Belgium', value: 'BE' },
    { label: 'Belize', value: 'BZ' },
    { label: 'Benin', value: 'BJ' },
    { label: 'Bhutan', value: 'BT' },
    { label: 'Bolivia', value: 'BO' },
    { label: 'Bosnia and Herzegovina', value: 'BA' },
    { label: 'Botswana', value: 'BW' },
    { label: 'Brazil', value: 'BR' },
    { label: 'Brunei Darussalam', value: 'BN' },
    { label: 'Bulgaria', value: 'BG' },
    { label: 'Burkina Faso', value: 'BF' },
    { label: 'Burundi', value: 'BI' },
    { label: 'Cambodia', value: 'KH' },
    { label: 'Cameroon', value: 'CM' },
    { label: 'Canada', value: 'CA' },
    { label: 'Cape Verde', value: 'CV' },
    { label: 'Central African Republic', value: 'CF' },
    { label: 'Chad', value: 'TD' },
    { label: 'Chile', value: 'CL' },
    { label: 'China', value: 'CN' },
    { label: 'Colombia', value: 'CO' },
    { label: 'Comoros', value: 'KM' },
    { label: 'Congo', value: 'CG' },
    { label: 'Costa Rica', value: 'CR' },
    { label: 'Croatia', value: 'HR' },
    { label: 'Cuba', value: 'CU' },
    { label: 'Cyprus', value: 'CY' },
    { label: 'Czech Republic', value: 'CZ' },
    { label: 'Denmark', value: 'DK' },
    { label: 'Djibouti', value: 'DJ' },
    { label: 'Dominica', value: 'DM' },
    { label: 'Dominican Republic', value: 'DO' },
    { label: 'Ecuador', value: 'EC' },
    { label: 'Egypt', value: 'EG' },
    { label: 'El Salvador', value: 'SV' },
    { label: 'Equatorial Guinea', value: 'GQ' },
    { label: 'Eritrea', value: 'ER' },
    { label: 'Estonia', value: 'EE' },
    { label: 'Eswatini', value: 'SZ' },
    { label: 'Ethiopia', value: 'ET' },
    { label: 'Fiji', value: 'FJ' },
    { label: 'Finland', value: 'FI' },
    { label: 'France', value: 'FR' },
    { label: 'Gabon', value: 'GA' },
    { label: 'Gambia', value: 'GM' },
    { label: 'Georgia', value: 'GE' },
    { label: 'Germany', value: 'DE' },
    { label: 'Ghana', value: 'GH' },
    { label: 'Greece', value: 'GR' },
    { label: 'Grenada', value: 'GD' },
    { label: 'Guatemala', value: 'GT' },
    { label: 'Guinea', value: 'GN' },
    { label: 'Guinea-Bissau', value: 'GW' },
    { label: 'Guyana', value: 'GY' },
    { label: 'Haiti', value: 'HT' },
    { label: 'Honduras', value: 'HN' },
    { label: 'Hungary', value: 'HU' },
    { label: 'Iceland', value: 'IS' },
    { label: 'India', value: 'IN' },
    { label: 'Indonesia', value: 'ID' },
    { label: 'Iran', value: 'IR' },
    { label: 'Iraq', value: 'IQ' },
    { label: 'Ireland', value: 'IE' },
    { label: 'Israel', value: 'IL' },
    { label: 'Italy', value: 'IT' },
    { label: 'Jamaica', value: 'JM' },
    { label: 'Japan', value: 'JP' },
    { label: 'Jordan', value: 'JO' },
    { label: 'Kazakhstan', value: 'KZ' },
    { label: 'Kenya', value: 'KE' },
    { label: 'Kiribati', value: 'KI' },
    { label: 'Kuwait', value: 'KW' },
    { label: 'Kyrgyzstan', value: 'KG' },
    { label: 'Laos', value: 'LA' },
    { label: 'Latvia', value: 'LV' },
    { label: 'Lebanon', value: 'LB' },
    { label: 'Lesotho', value: 'LS' },
    { label: 'Liberia', value: 'LR' },
    { label: 'Libya', value: 'LY' },
    { label: 'Liechtenstein', value: 'LI' },
    { label: 'Lithuania', value: 'LT' },
    { label: 'Luxembourg', value: 'LU' },
    { label: 'Madagascar', value: 'MG' },
    { label: 'Malawi', value: 'MW' },
    { label: 'Malaysia', value: 'MY' },
    { label: 'Maldives', value: 'MV' },
    { label: 'Mali', value: 'ML' },
    { label: 'Malta', value: 'MT' },
    { label: 'Mauritania', value: 'MR' },
    { label: 'Mauritius', value: 'MU' },
    { label: 'Mexico', value: 'MX' },
    { label: 'Moldova', value: 'MD' },
    { label: 'Monaco', value: 'MC' },
    { label: 'Mongolia', value: 'MN' },
    { label: 'Montenegro', value: 'ME' },
    { label: 'Morocco', value: 'MA' },
    { label: 'Mozambique', value: 'MZ' },
    { label: 'Myanmar', value: 'MM' },
    { label: 'Namibia', value: 'NA' },
    { label: 'Nauru', value: 'NR' },
    { label: 'Nepal', value: 'NP' },
    { label: 'Netherlands', value: 'NL' },
    { label: 'New Zealand', value: 'NZ' },
    { label: 'Nicaragua', value: 'NI' },
    { label: 'Niger', value: 'NE' },
    { label: 'Nigeria', value: 'NG' },
    { label: 'North Korea', value: 'KP' },
    { label: 'North Macedonia', value: 'MK' },
    { label: 'Norway', value: 'NO' },
    { label: 'Oman', value: 'OM' },
    { label: 'Pakistan', value: 'PK' },
    { label: 'Palau', value: 'PW' },
    { label: 'Panama', value: 'PA' },
    { label: 'Papua New Guinea', value: 'PG' },
    { label: 'Paraguay', value: 'PY' },
    { label: 'Peru', value: 'PE' },
    { label: 'Philippines', value: 'PH' },
    { label: 'Poland', value: 'PL' },
    { label: 'Portugal', value: 'PT' },
    { label: 'Qatar', value: 'QA' },
    { label: 'Romania', value: 'RO' },
    { label: 'Russia', value: 'RU' },
    { label: 'Rwanda', value: 'RW' },
    { label: 'Saudi Arabia', value: 'SA' },
    { label: 'Senegal', value: 'SN' },
    { label: 'Serbia', value: 'RS' },
    { label: 'Seychelles', value: 'SC' },
    { label: 'Sierra Leone', value: 'SL' },
    { label: 'Singapore', value: 'SG' },
    { label: 'Slovakia', value: 'SK' },
    { label: 'Slovenia', value: 'SI' },
    { label: 'Somalia', value: 'SO' },
    { label: 'South Africa', value: 'ZA' },
    { label: 'South Korea', value: 'KR' },
    { label: 'Spain', value: 'ES' },
    { label: 'Sri Lanka', value: 'LK' },
    { label: 'Sudan', value: 'SD' },
    { label: 'Suriname', value: 'SR' },
    { label: 'Sweden', value: 'SE' },
    { label: 'Switzerland', value: 'CH' },
    { label: 'Syria', value: 'SY' },
    { label: 'Taiwan', value: 'TW' },
    { label: 'Tajikistan', value: 'TJ' },
    { label: 'Tanzania', value: 'TZ' },
    { label: 'Thailand', value: 'TH' },
    { label: 'Timor-Leste', value: 'TL' },
    { label: 'Togo', value: 'TG' },
    { label: 'Tonga', value: 'TO' },
    { label: 'Tunisia', value: 'TN' },
    { label: 'Turkey', value: 'TR' },
    { label: 'Turkmenistan', value: 'TM' },
    { label: 'Uganda', value: 'UG' },
    { label: 'Ukraine', value: 'UA' },
    { label: 'United Arab Emirates', value: 'AE' },
    { label: 'United Kingdom', value: 'GB' },
    { label: 'United States', value: 'US' },
    { label: 'Uruguay', value: 'UY' },
    { label: 'Uzbekistan', value: 'UZ' },
    { label: 'Vanuatu', value: 'VU' },
    { label: 'Venezuela', value: 'VE' },
    { label: 'Vietnam', value: 'VN' },
    { label: 'Yemen', value: 'YE' },
    { label: 'Zambia', value: 'ZM' },
    { label: 'Zimbabwe', value: 'ZW' },
  ];
  yesOrNo = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  race = [
    { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { label: 'Asian', value: 'Asian' },
    { label: 'Black or African American', value: 'Black or African American' },
    {
      label: 'Native Hawaiian or other Pacific Islander',
      value: 'Native Hawaiian or other Pacific Islander',
    },
    { label: 'White', value: 'White' },
    { label: 'Multi-race', value: 'Multi-race' },
    { label: 'Other', value: 'Other' },
  ];

  ethnicity = [
    { label: 'Non Hispanic or Latino', value: 'Non Hispanic or Latino' },
    { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
  ];

  militaryStatus = [
      {
          "label": "--None--",
          "value": "None"
      },
      {
          "label": "Active Duty",
          "value": "Active Duty"
      },
      {
          "label": "Veteran",
          "value": "Veteran"
      },
      {
          "label": "Unknown/Not Reported",
          "value": "Unknown/Not Reported"
      },
      {
          "label": "Never Served in the Military",
          "value": "Never Served in the Military"
      }
  ];

  citizenshipStatus = [
    { label: 'U.S. Citizenship', value: 'U.S. Citizenship' },
    { label: 'Naturalized U.S. Citizen', value: 'Naturalized U.S. Citizen' },
    { label: 'Permanent Resident', value: 'Permanent Resident' },
    { label: 'Legal Resident', value: 'Legal Resident' },
    { label: 'Other', value: 'Other' },
  ];

  // Toaster state
  showToaster: boolean = false;
  toasterMessage: string = '';
  toasterType: 'success' | 'error' = 'success';
  // Success modal state
  showSuccessModal: boolean = false;
  showLoginModal: boolean = false;
  registeredEmailForLogin: string = '';
  countyOptions = [];
  addressLine: any[] = [];

  isProfileMode: boolean = false;
  hideFooter: boolean = false;
  addressSearchLoading: boolean = false;
  isPrefillingForm: boolean = false;
  isSubmitting: boolean = false;
  formSubmitted: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly location: Location,
    private readonly router: Router,
    private readonly apiService: FormApiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    private readonly loginModalService: LoginModalService
  ) {
    this.isProfileMode = this.router.url === '/profile';
    this.hideFooter = this.isProfileMode;
    this.registerForm = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        confirmEmail: [
          '',
          this.isProfileMode ? [] : [Validators.required, Validators.email],
        ],
        phone: ['', [Validators.required, Validators.pattern('^\\(\\d{3}\\) \\d{3}-\\d{4}$')]],
        certifyAccuracy: [false, this.isProfileMode ? [] : [Validators.requiredTrue]],
        addresses: this.fb.group({
        0: this.fb.group({
          addressLine: [''],
          apartmentNumber: [''],
          postalCode: ['', Validators.required],
          city: ['', Validators.required],
          state: ['', Validators.required],
          country: ['', Validators.required],
          county: [''],
          addressType: ['residential'],
        }),
        1: this.fb.group({
          addressLine: [''],
          apartmentNumber: [''],
          postalCode: [''],
          city: [''],
          state: [''],
          country: [''],
          county: [''],
          addressType: ['mailing'],
        }),
      }),
      contacts: this.fb.array([
        this.fb.group(
          {
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            dob: ['', [Validators.required, this.minAgeValidator(17)]],
            confirmDob: ['', this.isProfileMode ? [] : [Validators.required]],
            gender: ['', Validators.required],
            is_primary: [true],
            race: ['', Validators.required],
            ethinicity: ['', Validators.required],
            militaryStatus: ['', Validators.required],
            isMigrantFarmWorker: ['', Validators.required],
            isDisabled: ['', Validators.required],
            citizenship: ['', Validators.required],
            isReceivingIncome: ['', Validators.required],
            reasonForNoIncome: [''],
            ssn: ['', 
              [Validators.required, Validators.pattern('^\\d{3}-\\d{2}-\\d{4}$')],
              [this.checkIdentificationNumberValidator.bind(this)]
            ],
            confirmSsn: ['', [Validators.required, Validators.pattern('^\\d{3}-\\d{2}-\\d{4}$')]],
            identificationType: ['', Validators.required],
          },
          { validators: [this.ssnMatchValidator.bind(this), this.dobMatchValidator.bind(this)] }
        ),
      ]),
    },
    { validators: this.emailMatchValidator.bind(this) }
    );
  }

  private emailMatchValidator(group: AbstractControl): ValidationErrors | null {
    if (this.isProfileMode) return null;
    const email = group.get('email')?.value;
    const confirm = group.get('confirmEmail')?.value;
    if (!email || !confirm) return null;
    const e = String(email).trim().toLowerCase();
    const c = String(confirm).trim().toLowerCase();
    return e === c ? null : { emailMismatch: true };
  }

  private ssnMatchValidator(group: AbstractControl): ValidationErrors | null {
    if (this.isProfileMode) return null;
    const ssn = group.get('ssn')?.value;
    const confirmSsn = group.get('confirmSsn')?.value;
    if (!ssn || !confirmSsn) return null;
    return ssn === confirmSsn ? null : { ssnMismatch: true };
  }

  private dobMatchValidator(group: AbstractControl): ValidationErrors | null {
    if (this.isProfileMode) return null;
    const dob = group.get('dob')?.value;
    const confirmDob = group.get('confirmDob')?.value;
    if (!dob || !confirmDob) return null;
    const normalize = (value: unknown): string => {
      if (!value) return '';
      if (value instanceof Date && !isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      const raw = String(value).trim();
      const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
      const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
      return raw;
    };
    return normalize(dob) === normalize(confirmDob) ? null : { dobMismatch: true };
  }

  private minAgeValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isProfileMode || !control.value) return null;
      const age = this.calculateAgeFromDate(control.value);
      if (age === null) return null;
      return age >= minAge ? null : { minAge: { required: minAge, actual: age } };
    };
  }

  private checkIdentificationNumberValidator(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
  
    // Debounce so we don't call the API on every keystroke; cancel previous
    // request when value changes again (only one in-flight call per settled value).
    return timer(400).pipe(
      switchMap(() => {
        const value = control.value;
        // Skip empty or invalid format; clear cache so we re-check if they type again
        if (!value || !/^\d{3}-\d{2}-\d{4}$/.test(value)) {
          this.lastCheckedSsn = null;
          return of(null);
        }
        // Don't call API again for the same value (e.g. typing after full format does nothing)
        if (value === this.lastCheckedSsn) {
          return of(this.lastCheckResult);
        }
        return this.apiService.checkIdentificationNumber(value).pipe(
      map((response: any) => {
        if (response.statusCode === 200 && response.isAvailable === true) {
          if (response.hasActiveProgramApplication === true) {
            this.lastCheckedSsn = value;
            this.lastCheckResult = { hasActiveProgramApplication: true };
            return this.lastCheckResult;
          }
          this.lastCheckedSsn = value;
          this.lastCheckResult = null;
          return null;
        }
        this.lastCheckedSsn = value;
        this.lastCheckResult = { ssnExists: true };
        return this.lastCheckResult;
      }),
      catchError((error) => {
        // 409 = duplicate SSN → SHOW ERROR
        if (error?.status === 409) {
          this.lastCheckedSsn = value;
          this.lastCheckResult = { ssnExists: true };
          return of(this.lastCheckResult);
        }
        this.lastCheckedSsn = null;
  
        // Other errors → don’t block user
        return of(null);
      })
        );
      })
    );
  }

  ngOnInit() {
    window.scrollTo(0, 0);

    // Load Google Maps API and initialize AutocompleteService
    this.googleMapsLoader.load().then(() => {
      this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
    }).catch((error) => {
      console.error('Failed to load Google Maps API:', error);
    });

    // Setup debounced address search
    this.addressSearchSubject
      .pipe(
        debounceTime(150), // Reduced to 150ms for faster response
        distinctUntilChanged(), // Only search if query changed
        takeUntil(this.destroy$)
      )
      .subscribe((query: string) => {
        this.performAddressSearch(query);
      });

    // Setup phone number formatting
    this.registerForm.get('phone')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string | null) => {
        this.handlePhoneInput(value);
      });

    // Trigger change detection when certification checkbox changes so error message hides
    const certifyControl = this.registerForm.get('certifyAccuracy');
    if (certifyControl) {
      certifyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        setTimeout(() => this.cdr.detectChanges(), 0);
      });
      certifyControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        setTimeout(() => this.cdr.detectChanges(), 0);
      });
    }

    // Setup DOB and receiving income change handlers for conditional "Reason For No Income" field
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (contactGroup) {
      contactGroup.get('dob')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateMilitaryStatusForAge();
          this.updateReasonForNoIncomeField();
          this.cdr.detectChanges();
        });

      contactGroup.get('isReceivingIncome')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateReasonForNoIncomeField();
          this.cdr.detectChanges();
        });
      
      // Sync identificationNumber with ssn when ssn changes
      // The async validator will automatically run when ssn value changes
      contactGroup.get('ssn')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((ssnValue: string) => {
          const identificationNumberControl = contactGroup.get('identificationNumber');
          if (identificationNumberControl) {
            // Always sync, even if empty (to clear identificationNumber when ssn is cleared)
            identificationNumberControl.setValue(ssnValue || '', { emitEvent: false });
          }
        });
      
      // Initial check after form is built
      setTimeout(() => {
        this.updateMilitaryStatusForAge();
        this.updateReasonForNoIncomeField();
        // Sync initial ssn value to identificationNumber
        const ssnValue = contactGroup.get('ssn')?.value;
        if (ssnValue) {
          contactGroup.get('identificationNumber')?.setValue(ssnValue, { emitEvent: false });
        }
        this.cdr.detectChanges();
      }, 0);
    }

    if (this.isProfileMode) {
      this.registerForm.disable();
      this.loadProfile();
      // Also patch empty optional fields immediately in case form is already initialized
      setTimeout(() => {
        this.patchEmptyOptionalFields();
      }, 100);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadProfile() {
    this.isPrefillingForm = true;
    this.apiService.getProfile().subscribe({
      next: (response: any) => {
        this.prefillForm(response.profile);
        // Patch empty optional fields with "-" after prefilling
        this.patchEmptyOptionalFields();
        this.isPrefillingForm = false;
        this.triggerChangeDetection();
      },
      error: (error: any) => {
        // Even on error, patch empty optional fields if form exists
        this.patchEmptyOptionalFields();
        this.isPrefillingForm = false;
        this.triggerChangeDetection();
      },
    });
  }

  private prefillForm(profile: any) {
    // Format phone number if it exists
    const formattedPhone = profile.phone ? this.formatPhoneNumber(profile.phone) : '';
    
    this.registerForm.patchValue({
      email: profile.email,
      phone: formattedPhone,
    });

    const residentialAddress = profile.addresses.find(
      (addr: any) => addr.addressType === 'residential'
    );
    const mailingAddress = profile.addresses.find((addr: any) => addr.addressType === 'mailing');

    if (residentialAddress) {
      this.prefillAddressForm(residentialAddress, 0);
    }

    if (mailingAddress) {
      this.isMailingAddressDifferent = true;
      this.prefillAddressForm(mailingAddress, 1);
    }

    if (profile.contacts?.length) {
      const c = profile.contacts[0];
      
      // Map identificationType label back to value for form control
      const identificationTypeLabelToValue: { [key: string]: string } = {
        'Social Security Number (SSN)': 'ssn',
        'Taxpayer Identification Number (ITIN)': 'itin'
      };
      const identificationTypeValue = profile.identificationType 
        ? (identificationTypeLabelToValue[profile.identificationType] || profile.identificationType)
        : '';
      
      this.registerForm.get('contacts.0')?.patchValue({
        firstName: c.firstName,
        lastName: c.lastName,
        email: c.email,
        dob: FormDateUtils.parseDateOnly(c.dob) ?? '',
        gender: c.gender,
        race: c.race,
        ethinicity: c.ethinicity,
        militaryStatus: c.militaryStatus,
        isMigrantFarmWorker: c.isMigrantFarmWorker,
        isDisabled: c.isDisabled,
        citizenship: c.citizenship,
        isReceivingIncome: c.isReceivingIncome,
        reasonForNoIncome: profile.reasonForNoIncome || c.reasonForNoIncome || '',
        ssn: c.ssn,
        confirmSsn: this.isProfileMode ? c.ssn : '', // Prefill confirm only in profile mode (form disabled)
        identificationType: identificationTypeValue,
        identificationNumber:  c.ssn,
      });
      
      // Update the reasonForNoIncome field visibility and validators after prefilling
      // Also enforce military status rule for under-18 users
      setTimeout(() => {
        this.updateMilitaryStatusForAge();
        this.updateReasonForNoIncomeField();
      }, 0);
    }
  }

  onSubmit() {
    if (this.isProfileMode) return;
  
    this.formSubmitted = true;
  
    if (this.registerForm.pending) {
      console.log('Form pending, wait for async validators');
      return;
    }
  
    if (this.registerForm.invalid) {
      const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
      if (contactGroup?.errors?.['ssnMismatch']) {
        this.showToasterMessage('Identification numbers do not match. Please ensure both entries are identical.', 'error');
      }
      this.markAllFieldsAsTouched();
      this.triggerChangeDetection();
      this.scrollToFirstInvalidField();
      return;
    }
  
    // ✅ FORM IS VALID
    console.log('Form valid, calling register API');
  
    this.isSubmitting = true;
    const payload = this.createPayload();
  
    this.apiService.register(payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.showSuccessModal = true;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isSubmitting = false;
        const msg =
          error?.error?.message ||
          error?.message ||
          'Registration failed. Please try again.';
        this.showToasterMessage(msg, 'error');
      },
    });
  }
  

  private waitForAsyncValidatorsAndSubmit() {
    const maxWaitTime = 15000; // Maximum 15 seconds wait
    const checkInterval = 100; // Check every 100ms
    let elapsedTime = 0;
    let statusSubscription: any = null;

    // Find the SSN control which has the async validator
    const ssnControl = this.registerForm.get('contacts.0.ssn');
    
    // If SSN control exists and is pending, wait for it to complete via statusChanges
    if (ssnControl && ssnControl.pending) {
      console.log('Waiting for SSN async validator to complete...');
      
      statusSubscription = ssnControl.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!ssnControl.pending) {
            console.log('SSN async validator completed');
            if (statusSubscription) {
              statusSubscription.unsubscribe();
            }
            // Small delay to ensure form state is updated
            setTimeout(() => {
              this.updateAllControlsValidity();
              this.checkFormAndSubmit();
            }, 100);
          }
        });

      // Fallback: if validator takes too long, proceed anyway after max wait time
      setTimeout(() => {
        if (ssnControl.pending) {
          console.warn('Async validator timeout after', maxWaitTime, 'ms, proceeding with submission check');
          if (statusSubscription) {
            statusSubscription.unsubscribe();
          }
          this.updateAllControlsValidity();
          this.checkFormAndSubmit();
        }
      }, maxWaitTime);
    } else {
      // No specific pending control or control not found, use polling approach
      console.log('Using polling approach to wait for async validators...');
      const pollInterval = setInterval(() => {
        elapsedTime += checkInterval;
        this.updateAllControlsValidity();
        const stillPending = this.hasPendingAsyncValidators(this.registerForm);
        
        if (!stillPending) {
          console.log('All async validators completed after', elapsedTime, 'ms');
          clearInterval(pollInterval);
          this.checkFormAndSubmit();
        } else if (elapsedTime >= maxWaitTime) {
          console.warn('Async validators timeout after', maxWaitTime, 'ms, proceeding with submission check');
          clearInterval(pollInterval);
          this.checkFormAndSubmit();
        }
      }, checkInterval);
    }
  }

  private checkFormAndSubmit() {
    // Update validity one more time to ensure we have latest state
    
    // Check if form is valid - must be valid with no pending validators
    const isFormValid = this.registerForm.valid;
    const hasPendingValidators = this.hasPendingAsyncValidators(this.registerForm);
    
    // Log for debugging
    console.log('checkFormAndSubmit - Form valid:', isFormValid);
    console.log('checkFormAndSubmit - Has pending validators:', hasPendingValidators);
    
    // Only allow submission if:
    // 1. Form is valid (no sync or async errors - this includes SSN duplicate check)
    // 2. No pending validators (all async validators have completed)
    if (isFormValid && !hasPendingValidators) {
      console.log('Form is valid, submitting...');
      this.isSubmitting = true;
      const payload = this.createPayload();
      console.log('Submitting payload:', payload);
      this.apiService.register(payload).subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          this.showSuccessModal = true;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          this.isSubmitting = false;
          console.error('Registration error:', error);
          const errorMessage = error?.error?.message || error?.message || 'Registration failed. Please try again.';
          this.showToasterMessage(errorMessage, 'error');
        },
      });
    } else {
      console.log('Form is invalid, showing errors...');
      this.markAllFieldsAsTouched();
      // Force change detection to update error display
      this.triggerChangeDetection();
      // Scroll to first invalid field after change detection
      setTimeout(() => {
        this.scrollToFirstInvalidField();
        // Trigger another change detection after scrolling to ensure errors are visible
        setTimeout(() => {
          this.triggerChangeDetection();
        }, 200);
      }, 200);
    }
  }

  private triggerChangeDetection(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /**
   * Clear address search results and loading state
   */
  private clearAddressSearch(): void {
    this.addressLine = [];
    this.addressSearchLoading = false;
    this.triggerChangeDetection();
  }

  /**
   * Set address search loading state
   */
  private setAddressSearchLoading(loading: boolean): void {
    this.addressSearchLoading = loading;
    this.triggerChangeDetection();
  }

  /**
   * Initialize Google Maps AutocompleteService if available
   * @deprecated Use GoogleMapsLoaderService instead
   */
  private initializeGoogleMapsService(): boolean {
    if (this.googleMapsLoader.isLoaded()) {
      this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
      return this.autocompleteService !== null;
    }
    return false;
  }

  /**
   * Prefill address form group with address data
   */
  private prefillAddressForm(addressData: any, addressIndex: number): void {
    this.registerForm.get(`addresses.${addressIndex}`)?.patchValue({
      addressLine: addressData.addressLine,
      apartmentNumber: addressData.apartmentNumber || addressData.apartment_number || '',
      postalCode: addressData.postalCode,
      city: addressData.city,
      state: addressData.state,
      country: addressData.country,
      county: addressData.county,
    });
  }

  /**
   * Check if error should be shown for a control
   */
  private shouldShowErrorForControl(control: AbstractControl): boolean {
    const isDirtyOrTouched = control.dirty || control.touched;
    return isDirtyOrTouched || this.formSubmitted;
  }

  private forEachControlRecursive(
    formGroup: FormGroup,
    callback: (control: AbstractControl) => void,
    handleArrayControl?: (arrayControl: AbstractControl) => void
  ): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        callback(control);
        if (control instanceof FormGroup) {
          this.forEachControlRecursive(control, callback, handleArrayControl);
        } else if (control instanceof FormArray) {
          control.controls.forEach((arrayControl: any) => {
            if (arrayControl instanceof FormGroup) {
              this.forEachControlRecursive(arrayControl, callback, handleArrayControl);
            } else if (handleArrayControl) {
              handleArrayControl(arrayControl);
            }
          });
        }
      }
    });
  }

  private updateAllControlsValidity() {
    this.forEachControlRecursive(
      this.registerForm,
      (control) => control.updateValueAndValidity({ emitEvent: false }),
      (arrayControl) => arrayControl.updateValueAndValidity({ emitEvent: false })
    );
  }

  private hasPendingAsyncValidators(form: FormGroup): boolean {
    let hasPending = false;
    this.forEachControlRecursive(
      form,
      (control) => {
        if (control.pending) {
          hasPending = true;
        }
      }
    );
    return hasPending;
  }

  private markAllFieldsAsTouched() {
    this.forEachControlRecursive(
      this.registerForm,
      (control) => control.markAsTouched(),
      (arrayControl) => arrayControl.markAsTouched()
    );
  }

  onCancel() {
    // this.location.back();
    this.router.navigate(['/card-checkin']);
  }

  onMailingAddressChange(value: boolean) {
    this.isMailingAddressDifferent = value;
    const mailingAddressGroup = this.registerForm.get('addresses.1') as FormGroup;
    const mailingAddressFields = ['postalCode', 'city', 'state', 'country', 'county'];
    
    mailingAddressFields.forEach((fieldName) => {
      const control = mailingAddressGroup.get(fieldName);
      if (control) {
        if (value) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
    // Note: apartmentNumber is optional, so no validators needed
  }

  private createPayload() {
    const formValue = this.registerForm.value;
    const addresses = [];

    addresses.push({
      ...formValue.addresses[0],
      addressType: 'residential',
    });

    if (this.isMailingAddressDifferent) {
      addresses.push({
        ...formValue.addresses[1],
        addressType: 'mailing',
      });
    }

    // Remove formatting from phone number for API (keep only digits)
    const phoneDigits = formValue.phone ? formValue.phone.replace(/\D/g, '') : '';

    // Map identificationType value to label
    const identificationTypeMap: { [key: string]: string } = {
      'ssn': 'Social Security Number (SSN)',
      'itin': 'Taxpayer Identification Number (ITIN)'
    };

    // Get identification data from first contact
    const firstContact = formValue.contacts[0] || {};
    const identificationTypeValue = firstContact.identificationType || '';
    const identificationTypeLabel = identificationTypeValue ? identificationTypeMap[identificationTypeValue] || identificationTypeValue : '';
    const identificationNumber = firstContact.ssn || firstContact.identificationNumber || '';

    const normalizedEmail = `${formValue.email}`.trim().toLowerCase();
    return {
      email: normalizedEmail,
      phone: phoneDigits,
      addresses: addresses,
      identificationType: identificationTypeLabel,
      identificationNumber: identificationNumber,
      reasonForNoIncome: formValue.contacts[0].reasonForNoIncome || '',
      isConfirmed: formValue.certifyAccuracy === true,

      contacts: formValue.contacts.map((contact: any) => ({
        ...contact,
        email: normalizedEmail,
        dob: contact.dob ? FormDateUtils.formatDate(contact.dob, 'YYYY-MM-DD') : null,
        identificationType: contact.identificationType ? identificationTypeMap[contact.identificationType] || contact.identificationType : '',
        identificationNumber: contact.ssn || contact.identificationNumber || '',
      })),
    };
  }

  get isFormValid(): boolean {
    return this.registerForm.valid;
  }

  getFieldError(fieldName: string): string {
    const control = this.getNestedControl(fieldName);
    if (!control) return '';
    
    const shouldShowError = this.shouldShowErrorForControl(control);
    
    // Get user-friendly field label
    const fieldLabel = this.fieldLabels[fieldName] || this.getDefaultFieldLabel(fieldName);
    
    // Certification checkbox uses Validators.requiredTrue which emits { required: true }
    if (fieldName === 'certifyAccuracy' && (control.errors?.['required'] || control.errors?.['requiredTrue']) && shouldShowError) {
      return 'You must check this box to certify that the information provided is true and accurate to continue.';
    }
    if (control.errors?.['required'] && shouldShowError) {
      return `${fieldLabel} is required`;
    }
    if (control.errors?.['requiredTrue'] && shouldShowError) {
      return 'You must certify that the information provided is true and accurate to continue.';
    }
    if (control.errors?.['email'] && shouldShowError) {
      return 'Please enter a valid email address';
    }
    if (control.errors?.['pattern'] && shouldShowError) {
      if (fieldName === 'contacts.0.ssn' || fieldName === 'contacts.0.confirmSsn') {
        return 'Please enter a valid format: XXX-XX-XXXX';
      }
      if (fieldName === 'contacts.0.identificationType') {
        return 'Please select a valid identification type';
      }
     
      if (fieldName === 'phone') {
        return 'Please enter a valid format: (XXX) XXX-XXXX';
      }
      return `${fieldLabel} has invalid format`;
    }
    if (control.errors?.['hasActiveProgramApplication'] && shouldShowError) {
      return 'This person already has an active program application and cannot register.';
    }
    if (control.errors?.['ssnExists'] && shouldShowError) {
      return 'This identification number already exists.';
    }
    if (control.errors?.['minAge'] && shouldShowError) {
      return 'You must be 17 years or older to create an account.';
    }
    if (fieldName === 'confirmEmail' && this.registerForm?.errors?.['emailMismatch'] && shouldShowError) {
      return 'Emails do not match. Please enter the same email in both fields.';
    }
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (fieldName === 'contacts.0.confirmSsn' && contactGroup?.errors?.['ssnMismatch'] && shouldShowError) {
      return 'Identification numbers do not match. Please ensure both entries are identical.';
    }
    if (fieldName === 'contacts.0.confirmDob' && contactGroup?.errors?.['dobMismatch'] && shouldShowError) {
      return 'Dates of birth do not match. Please ensure both entries are identical.';
    }
    return '';
  }

  private getDefaultFieldLabel(fieldName: string): string {
    // Extract the last part of the field name (e.g., "race" from "contacts.0.race")
    const parts = fieldName.split('.');
    const lastPart = parts[parts.length - 1];
    
    // Convert camelCase to Title Case
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.getNestedControl(fieldName);
    if (!control) return false;
    
    if (!this.shouldShowErrorForControl(control)) return false;

    if (fieldName === 'confirmEmail' && this.registerForm?.errors?.['emailMismatch']) {
      return true;
    }
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (fieldName === 'contacts.0.confirmSsn' && contactGroup?.errors?.['ssnMismatch']) {
      return true;
    }
    if (fieldName === 'contacts.0.confirmDob' && contactGroup?.errors?.['dobMismatch']) {
      return true;
    }

    // Only return true if there are actual validation errors
    return !!(control.errors && Object.keys(control.errors).length > 0);
  }

  private getNestedControl(fieldName: string) {
    const parts = fieldName.split('.');
    let control: any = this.registerForm;

    for (const part of parts) {
      if (control instanceof FormArray) {
        control = control.at(parseInt(part));
      } else {
        control = control.get(part);
      }
      if (!control) break;
    }

    return control;
  }

  private scrollToFirstInvalidField() {
    // Find the first invalid field
    const firstInvalidField = this.findFirstInvalidField();
    
    if (!firstInvalidField) {
      return;
    }

    // Wait for DOM to update
    requestAnimationFrame(() => {
      setTimeout(() => {
        const { targetElement, inputElement } = this.findElementsForField(firstInvalidField);
        this.scrollAndFocusElement(targetElement, inputElement);
      }, 200);
    });
  }

  private findElementsForField(fieldPath: string): { targetElement: HTMLElement | null; inputElement: HTMLElement | null } {
    const fieldParts = fieldPath.split('.');
    const fieldName = fieldParts[fieldParts.length - 1];
    
    const formElement = this.findFormElementByPath(fieldPath, fieldName, fieldParts);
    
    if (formElement) {
      return this.findTargetAndInputElements(formElement);
    }

    // Fallback: try to find by any selector
    return this.findFallbackElements(fieldName);
  }

  private findFormElementByPath(fieldPath: string, fieldName: string, fieldParts: string[]): HTMLElement | null {
    const allFieldsWithName = document.querySelectorAll(`[formControlName="${fieldName}"]`);
    
    if (allFieldsWithName.length === 0) {
      return null;
    }

    if (fieldPath.startsWith('addresses.')) {
      return this.findAddressFormElement(fieldParts, fieldName);
    }
    
    if (fieldPath.startsWith('contacts.')) {
      return this.findContactFormElement(fieldParts, fieldName);
    }
    
    return this.findTopLevelFormElement(allFieldsWithName);
  }

  private findFormElementBySelector(
    parentSelector: string,
    groupIndex: string,
    fieldName: string
  ): HTMLElement | null {
    const parentElement = document.querySelector(parentSelector);
    if (!parentElement) {
      return null;
    }
    
    const groupElement = parentElement.querySelector(`[formGroupName="${groupIndex}"]`);
    if (!groupElement) {
      return null;
    }
    
    return groupElement.querySelector(`[formControlName="${fieldName}"]`) as HTMLElement;
  }

  private findAddressFormElement(fieldParts: string[], fieldName: string): HTMLElement | null {
    const addressIndex = fieldParts[1];
    return this.findFormElementBySelector('[formGroupName="addresses"]', addressIndex, fieldName);
  }

  private findContactFormElement(fieldParts: string[], fieldName: string): HTMLElement | null {
    const contactIndex = fieldParts[1];
    return this.findFormElementBySelector('[formArrayName="contacts"]', contactIndex, fieldName);
  }

  private findTopLevelFormElement(allFieldsWithName: NodeListOf<Element>): HTMLElement {
    const topLevelElement = Array.from(allFieldsWithName).find(el => {
      const parent = el.closest('[formGroupName], [formArrayName]');
      return !parent;
    });
    
    return (topLevelElement as HTMLElement) || (allFieldsWithName[0] as HTMLElement);
  }

  private findTargetAndInputElements(formElement: HTMLElement): { targetElement: HTMLElement | null; inputElement: HTMLElement | null } {
    const inputElement = formElement.querySelector('input, textarea, select') as HTMLElement;
    let targetElement = formElement.closest('.flex-col') as HTMLElement;
    
    if (!targetElement) {
      targetElement = formElement.closest('.w-full') as HTMLElement || formElement;
    }
    
    return { targetElement, inputElement };
  }

  private findFallbackElements(fieldName: string): { targetElement: HTMLElement | null; inputElement: HTMLElement | null } {
    const fallbackElement = document.querySelector(`[formControlName="${fieldName}"]`) as HTMLElement;
    
    if (!fallbackElement) {
      return { targetElement: null, inputElement: null };
    }
    
    const targetElement = fallbackElement.closest('.flex-col, .w-full') as HTMLElement || fallbackElement;
    const inputElement = fallbackElement.querySelector('input, textarea, select') as HTMLElement;
    
    return { targetElement, inputElement };
  }

  private scrollAndFocusElement(targetElement: HTMLElement | null, inputElement: HTMLElement | null): void {
    if (!targetElement) {
      return;
    }

    targetElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });
    
    if (this.isFocusableInput(inputElement)) {
      setTimeout(() => {
        try {
          inputElement!.focus();
        } catch (e) {
          // Ignore focus errors (e.g., if element is not focusable)
        }
      }, 300);
    }
  }

  private isFocusableInput(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    
    const tagName = element.tagName;
    return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
  }

  private findFirstInvalidField(): string | null {
    // Check top-level fields first
    if (this.registerForm?.errors?.['emailMismatch']) {
      return 'confirmEmail';
    }
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (contactGroup?.errors?.['ssnMismatch']) {
      return 'contacts.0.confirmSsn';
    }
    if (contactGroup?.errors?.['dobMismatch']) {
      return 'contacts.0.confirmDob';
    }
    const topLevelFields = ['email', 'confirmEmail', 'phone', 'certifyAccuracy'];
    for (const fieldName of topLevelFields) {
      const control = this.registerForm.get(fieldName);
      if (control?.invalid) {
        return fieldName;
      }
    }

    // Check address fields
    const addressFields = ['postalCode', 'city', 'state', 'country', 'county'];
    for (const fieldName of addressFields) {
      const control = this.registerForm.get(`addresses.0.${fieldName}`);
      if (control?.invalid) {
        return `addresses.0.${fieldName}`;
      }
    }

    // Check contact fields
    const contactFields = ['firstName', 'lastName', 'dob', 'gender', 'race', 'ethinicity', 'militaryStatus',
      'isMigrantFarmWorker', 'isDisabled', 'citizenship', 'isReceivingIncome', 'ssn', 'confirmSsn', 'identificationType'];
    for (const fieldName of contactFields) {
      const control = this.registerForm.get(`contacts.0.${fieldName}`);
      if (control?.invalid) {
        return `contacts.0.${fieldName}`;
      }
    }

    // Fallback: find any invalid field recursively
    return this.findInvalidFieldRecursive(this.registerForm);
  }

  private findInvalidFieldRecursive(formGroup: FormGroup, prefix: string = ''): string | null {
    for (const key in formGroup.controls) {
      const control = formGroup.get(key);
      if (!control) continue;

      const fieldPath = this.buildFieldPath(prefix, key);
      const invalidField = this.checkControlForInvalidField(control, fieldPath);
      
      if (invalidField) {
        return invalidField;
      }
    }
    return null;
  }

  private buildFieldPath(prefix: string, key: string): string {
    return prefix ? `${prefix}.${key}` : key;
  }

  private checkControlForInvalidField(control: AbstractControl, fieldPath: string): string | null {
    if (this.isSimpleInvalidControl(control)) {
      return fieldPath;
    }

    if (control instanceof FormGroup) {
      return this.findInvalidFieldRecursive(control, fieldPath);
    }

    if (control instanceof FormArray) {
      return this.findInvalidFieldInFormArray(control, fieldPath);
    }

    return null;
  }

  private isSimpleInvalidControl(control: AbstractControl): boolean {
    return control.invalid && !(control instanceof FormGroup || control instanceof FormArray);
  }

  private findInvalidFieldInFormArray(formArray: FormArray, fieldPath: string): string | null {
    for (let i = 0; i < formArray.length; i++) {
      const arrayControl = formArray.at(i);
      const arrayFieldPath = `${fieldPath}.${i}`;
      
      if (arrayControl instanceof FormGroup) {
        const nested = this.findInvalidFieldRecursive(arrayControl, arrayFieldPath);
        if (nested) {
          return nested;
        }
      } else if (arrayControl.invalid) {
        return arrayFieldPath;
      }
    }
    return null;
  }

  onPhoneInputChange(value: string) {
    // Immediately handle phone input to prevent typing beyond 10 digits
    this.handlePhoneInput(value);
  }

  onFieldBlur(fieldName: string) {
    const control = this.getNestedControl(fieldName);
    if (control) {
      control.markAsTouched();
    }
  }

  onConfirmSsnBlur() {
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (!contactGroup) return;
    const ssn = contactGroup.get('ssn')?.value;
    const confirmSsn = contactGroup.get('confirmSsn')?.value;
    if (ssn && confirmSsn && ssn !== confirmSsn) {
      this.showToasterMessage('Identification numbers do not match. Please ensure both entries are identical.', 'error');
    }
  }

  onCertifyAccuracyChange(checked: boolean) {
    this.registerForm.get('certifyAccuracy')?.setValue(checked, { emitEvent: true });
    setTimeout(() => this.cdr.detectChanges(), 0);
  }

  showToasterMessage(message: string, type: 'success' | 'error') {
    this.toasterMessage = message;
    this.toasterType = type;
    this.showToaster = true;
  }

  closeToaster() {
    this.showToaster = false;
  }

  onLoginFromSuccessModal() {
    this.showSuccessModal = false;
    this.registeredEmailForLogin = String(this.registerForm.get('email')?.value || '').trim();
    this.showLoginModal = true;
    this.cdr.detectChanges();
  }

  onLoginModalClosed(): void {
    this.showLoginModal = false;
  }

  openLoginForExistingAccount(): void {
    this.registeredEmailForLogin = '';
    this.showLoginModal = true;
    this.cdr.detectChanges();
  }

  onAutoCompleteInputChange(event: any) {
    // Extract query from event - handle multiple possible formats
    let query = '';
    
    if (event?.target?.value !== undefined) {
      query = event.target.value;
    } else if (event?.value !== undefined) {
      query = event.value;
    } else if (typeof event === 'string') {
      query = event;
    } else {
      // Fallback: try to get value from form control
      const control = this.registerForm.get('addresses.0.addressLine');
      if (control) {
        query = control.value || '';
      }
    }

    // Convert to string and trim
    query = String(query || '').trim();


    // Clear results if query is too short (minimum 2 characters)
    if (!query || query.length < 2) {
      this.clearAddressSearch();
      return;
    }

    // Show loading state immediately for better UX
    this.setAddressSearchLoading(true);

    // Emit query to debounced subject - this will trigger search after user stops typing
    this.addressSearchSubject.next(query);
  }

  private performAddressSearch(query: string) {
    // Double-check query validity
    const trimmedQuery = query?.trim() || '';
    if (!trimmedQuery || trimmedQuery.length < 2) {
      this.clearAddressSearch();
      return;
    }

    // Use cached service or get from loader
    if (!this.autocompleteService) {
      if (this.googleMapsLoader.isLoaded()) {
        this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
      } else {
        // Retry after a short delay if Google Maps isn't loaded
        setTimeout(() => {
          if (this.googleMapsLoader.isLoaded()) {
            this.autocompleteService = this.googleMapsLoader.getAutocompleteService();
            if (this.autocompleteService) {
              this.performAddressSearch(query);
            } else {
              this.setAddressSearchLoading(false);
            }
          } else {
            this.setAddressSearchLoading(false);
          }
        }, 100);
        return;
      }
    }

    // Ensure loading state is set
    this.setAddressSearchLoading(true);

    // Make the API call
    if (!this.autocompleteService) {
      return;
    }
    
    this.autocompleteService.getPlacePredictions(
      {
        input: trimmedQuery,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      },
      (predictions, status) => {
        this.setAddressSearchLoading(false);

        if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
          // Create a new array reference for OnPush change detection
          // This is critical - OnPush only detects reference changes
          const newAddressLine = predictions.map((p) => ({
            label: p.description,
            value: p.description,
            id: p.place_id,
          }));
          
          // Assign new array reference to trigger change detection
          this.addressLine = [...newAddressLine];
        } else {
          this.addressLine = [];
        }
        
          // Force change detection to ensure component updates
        this.triggerChangeDetection();
        
        // Additional change detection after a microtask to ensure UI updates
        setTimeout(() => {
          this.triggerChangeDetection();
        }, 0);
      }
    );
  }

  onAddressSelected(event: any, addressIndex: number) {
    const placeId = event.value?.id;

    const service = new google.maps.places.PlacesService(document.createElement('div'));

    service.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'address_components'],
      },
      (place: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          this.extractAddress(place, addressIndex);
        }
      }
    );
  }

  private updateControlValue(control: AbstractControl | null, value: any): void {
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    }
  }

  /**
   * Handle phone input and enforce 10 digit limit
   */
  private handlePhoneInput(value: string | null): void {
    if (value === null || value === undefined) {
      return;
    }
    
    // Extract digits only
    const digits = value.replace(/\D/g, '');
    
    // Strictly limit to 10 digits - no more allowed
    if (digits.length > 10) {
      const phoneControl = this.registerForm.get('phone');
      if (phoneControl) {
        const truncated = digits.slice(0, 10);
        const formatted = this.formatPhoneNumber(truncated);
        phoneControl.setValue(formatted, { emitEvent: false });
        return;
      }
    }
    
    // Format the phone number
    const formatted = this.formatPhoneNumber(value);
    const phoneControl = this.registerForm.get('phone');
    
    // Only update if the formatted value is different
    if (phoneControl && formatted !== value) {
      phoneControl.setValue(formatted, { emitEvent: false });
    }
  }

  /**
   * Format phone number to (xxx) xxx-xxxx format
   * Only formats up to 10 digits - no more allowed
   */
  private formatPhoneNumber(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Strictly limit to 10 digits - this is the maximum
    const limitedDigits = digits.slice(0, 10);
    
    // Format based on length
    if (limitedDigits.length === 0) {
      return '';
    } else if (limitedDigits.length <= 3) {
      return `(${limitedDigits}`;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  }

  extractAddress(place: any, index: number) {
    const mapped = {
      postalCode: '',
      city: '',
      state: '',
      country: '',
      county: '',
    };

    for (const c of place.address_components) {
      if (c.types.includes('postal_code')) {
        mapped.postalCode = c.long_name;
      }

      if (c.types.includes('locality')) {
        mapped.city = c.long_name;
      }

      if (c.types.includes('administrative_area_level_1')) {
        mapped.state = c.short_name;
      }

      if (c.types.includes('country')) {
        mapped.country = c.long_name;
      }

      if (c.types.includes('administrative_area_level_2')) {
        mapped.county = c.long_name.replace(' County', '');
      }
    }

    const addressControl = (this.registerForm.get('addresses') as FormGroup).get(index.toString());

    Object.keys(mapped).forEach((key) => {
      const control = addressControl?.get(key) || null;
      this.updateControlValue(control, mapped[key as keyof typeof mapped]);
    });

    this.triggerChangeDetection();
  }

  /**
   * Check if "Reason For No Income" textarea should be shown
   * Shows when age >= 18 (18 or older) AND receiving income is "no"
   */
  shouldShowReasonForNoIncome(): boolean {
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (!contactGroup) {
      return false;
    }

    const dobControl = contactGroup.get('dob');
    const receivingIncomeControl = contactGroup.get('isReceivingIncome');
    
    if (!dobControl || !receivingIncomeControl) {
      return false;
    }
    
    const dobValue = dobControl.value;
    const receivingIncomeValue = receivingIncomeControl.value;
    
    
    if (!dobValue || receivingIncomeValue === null || receivingIncomeValue === undefined || receivingIncomeValue === '') {
      return false;
    }
    
    // Check if receiving income is "no" - handle both boolean (false) and string ("no") values
    // yesOrNo options use boolean: { label: 'Yes', value: true }, { label: 'No', value: false }
    const isReceivingIncomeNo = receivingIncomeValue === false || 
                                receivingIncomeValue === 'no' || 
                                String(receivingIncomeValue).toLowerCase().trim() === 'no';
    if (!isReceivingIncomeNo) {
      return false;
    }
    
    // Calculate age
    const age = this.calculateAgeFromDate(dobValue);
    
    // Show if age is 18 or more (>= 18)
    const shouldShow = age !== null && age >= 18;
    return shouldShow;
  }

  /**
   * Calculate age from date of birth
   */
  private calculateAgeFromDate(dobValue: any): number | null {
    if (!dobValue) {
      return null;
    }

    let birthDate: Date | null = null;

    // If it's already a Date object
    if (dobValue instanceof Date) {
      birthDate = dobValue;
    } 
    // If it's a string, try to parse it
    else if (typeof dobValue === 'string') {
      // Try parsing as ISO string first (handles yyyy-MM-dd and ISO format)
      birthDate = new Date(dobValue);
      
      // If that fails, try parsing common date formats
      if (isNaN(birthDate.getTime())) {
        // Try yyyy-MM-dd format explicitly
        const isoMatch = dobValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          birthDate = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
        }
        
        // If still invalid, try DD/MM/YYYY format
        if (isNaN(birthDate.getTime())) {
          const parts = dobValue.split('/');
          if (parts.length === 3) {
            // Try DD/MM/YYYY
            birthDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            if (isNaN(birthDate.getTime())) {
              // Try MM/DD/YYYY
              birthDate = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
            }
          }
        }
      }
    }
    // If it's a number (timestamp)
    else if (typeof dobValue === 'number') {
      birthDate = new Date(dobValue);
    }
    // Try to convert to Date anyway
    else {
      birthDate = new Date(dobValue);
    }
    
    if (!birthDate || isNaN(birthDate.getTime())) {
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Patch empty optional fields with "-" when in profile mode
   */
  private patchEmptyOptionalFields(): void {
    if (!this.isProfileMode) {
      return;
    }

    // Optional fields that should show "-" if empty
    const optionalFields = [
      'addresses.0.addressLine',
      'addresses.0.apartmentNumber',
      'addresses.0.county',
      'addresses.1.addressLine',
      'addresses.1.apartmentNumber',
      'addresses.1.county',
    ];

    optionalFields.forEach((fieldPath) => {
      const control = this.registerForm.get(fieldPath);
      if (control) {
        const value = control.value;
        // If value is empty, null, or undefined, set it to "-"
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          control.patchValue('-', { emitEvent: false });
        }
      }
    });
  }

  /**
   * Get display value for optional disabled fields - show "-" if empty
   */
  getDisplayValue(controlPath: string, isRequired: boolean = false): string {
    if (!this.isProfileMode || isRequired) {
      // Not in profile mode or field is required, return actual value
      const control = this.registerForm.get(controlPath);
      return control?.value || '';
    }
    
    // In profile mode and field is optional
    const control = this.registerForm.get(controlPath);
    const value = control?.value;
    
    // Show "-" if value is empty, null, or undefined
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return '-';
    }
    
    return value;
  }

  /**
   * Get military status options filtered by age.
   * Users under 18 can only select "Never Served in the Military".
   */
  getMilitaryStatusOptions(): Array<{ label: string; value: string }> {
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    const dobValue = contactGroup?.get('dob')?.value;
    const age = this.calculateAgeFromDate(dobValue);

    if (age !== null && age < 18) {
      return [{ label: 'Never Served in the Military', value: 'Never Served in the Military' }];
    }
    return this.militaryStatus;
  }

  /**
   * Enforce military status rule: users under 18 must have "Never Served in the Military".
   */
  private updateMilitaryStatusForAge(): void {
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (!contactGroup) return;

    const dobValue = contactGroup.get('dob')?.value;
    const militaryControl = contactGroup.get('militaryStatus');
    if (!militaryControl) return;

    const age = this.calculateAgeFromDate(dobValue);
    if (age !== null && age < 18) {
      const neverServed = 'Never Served in the Military';
      if (militaryControl.value !== neverServed) {
        militaryControl.setValue(neverServed, { emitEvent: false });
      }
    }
  }

  /**
   * Update "Reason For No Income" field visibility and validators
   */
  private updateReasonForNoIncomeField(): void {
    const contactGroup = this.registerForm.get('contacts.0') as FormGroup;
    if (!contactGroup) {
      return;
    }

    const shouldShow = this.shouldShowReasonForNoIncome();
    const reasonControl = contactGroup.get('reasonForNoIncome');
    
    if (shouldShow) {
      // Add control if it doesn't exist
      if (!reasonControl) {
        const newControl = this.fb.control('', Validators.required);
        // If form is disabled (profile mode), disable the new control too
        if (this.registerForm.disabled) {
          newControl.disable();
        }
        contactGroup.addControl('reasonForNoIncome', newControl);
      } else {
        // Update validators to make it required
        reasonControl.setValidators(Validators.required);
        // Only enable if the parent form is not disabled (not in profile mode)
        if (!this.registerForm.disabled) {
          reasonControl.enable();
        } else {
          reasonControl.disable();
        }
        reasonControl.updateValueAndValidity();
      }
    } else {
      // Remove validators if field exists but shouldn't be shown
      if (reasonControl) {
        reasonControl.clearValidators();
        reasonControl.updateValueAndValidity();
        // Clear the value when hiding
        reasonControl.setValue('');
      }
    }
    
    // Trigger change detection
    this.cdr.detectChanges();
  }
}
